import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Target } from "lucide-react";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";

// Session types with a translated label under team.sessionTypes.*
const SESSION_TYPE_LABELS = new Set(["training", "match", "recovery"]);

const SESSION_TYPE_BG_COLORS = {
  training: "#FEF3C7",
  match: "#E0E7FF",
  recovery: "#CCFBF1",
};

const SESSION_TYPE_TEXT_COLORS = {
  training: "#B45309",
  match: "#4F46E5",
  recovery: "#0F766E",
};

// Short date label (e.g. "5 Dec") from an ISO/date string.
const formatLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return String(value);
  return d.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
  });
};

const CustomTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  const type = point.type;
  return (
    <div className="min-w-[150px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <p className="text-[13px] font-semibold text-[#111827]">
          {point.sessionName || t("team.overview.fallbackSessionName")}
        </p>
        {type && (
          <span
            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
            style={{
              backgroundColor: SESSION_TYPE_BG_COLORS[type] || "#F3F4F6",
              color: SESSION_TYPE_TEXT_COLORS[type] || "#6B7280",
            }}
          >
            {SESSION_TYPE_LABELS.has(type)
              ? t(`team.sessionTypes.${type}`)
              : type}
          </span>
        )}
      </div>
      <p className="mb-2 text-[11px] text-[#9CA3AF]">
        {formatLabel(point.date || point.name)}
      </p>
      <div className="border-t border-[#F3F4F6] pt-2">
        <p className="mb-0.5 text-[10px] uppercase tracking-wide text-[#9CA3AF]">
          {t("team.headersChart.headers")}
        </p>
        <p className="text-lg font-bold text-[#111827]">{point.headers}</p>
      </div>
    </div>
  );
};

const TeamHeadersChart = ({ data = [], threshold = 10, loading = false }) => {
  const { t } = useTranslation();
  const chartData = (data || []).map((d) => ({
    ...d,
    label: formatLabel(d.date || d.name),
  }));
  const hasData = chartData.some((d) => d.headers > 0);

  return (
    <div>
      {/* Title */}
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[#111827]">
          {t("team.headersChart.title")}
        </h3>
        <p className="text-xs text-[#6B7280]">
          {t("team.headersChart.subtitle", { threshold })}
        </p>
      </div>

      {loading ? (
        <div className="h-[200px] w-full animate-pulse rounded-lg bg-[#F3F4F6]" />
      ) : !hasData ? (
        <div className="flex h-[200px] flex-col items-center justify-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
            <Target size={28} className="text-[#E07A3A]" />
          </div>
          <h4 className="mb-1 text-sm font-semibold text-[#111827]">
            {t("team.headersChart.emptyTitle")}
          </h4>
          <p className="max-w-[240px] text-xs leading-relaxed text-[#6B7280]">
            {t("team.headersChart.emptyBody")}
          </p>
        </div>
      ) : (
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
              barCategoryGap="20%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F3F4F6"
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                allowDecimals={false}
                dx={-5}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "#F9FAFB" }}
              />
              <Bar
                dataKey="headers"
                name={t("team.headersChart.headers")}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill="#E07A3A" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default TeamHeadersChart;
